/**
 * VoiceRecorder — reusable record-and-transcribe primitive shared by the
 * voice-fill modals (intake, incident, MAR). Wraps MediaRecorder, the timer,
 * mic-permission UX, and the POST to a chosen endpoint.
 *
 * Hook usage (NOT a component) — gives the parent full control over layout.
 *
 *   const rec = useVoiceRecorder({
 *     endpoint: "/ai/voice-fill/incident",
 *     extra: { client_id: clientId },
 *     onResult: (data) => setResult(data),
 *   });
 *
 *   <button onClick={rec.toggle}>{rec.isRecording ? "Stop" : "Start"}</button>
 *   {rec.elapsed > 0 && <span>{rec.mmss}</span>}
 */
import { useCallback, useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";

const DEFAULT_LIMIT_MS = 5 * 60 * 1000;

export default function useVoiceRecorder({
  endpoint,
  extra = {},
  onResult,
  limitMs = DEFAULT_LIMIT_MS,
  filenamePrefix = "voice",
  timeoutMs = 90000,
}) {
  const [state, setState] = useState("idle"); // idle | recording | processing
  const [elapsed, setElapsed] = useState(0);
  const mediaRef = useRef(null);
  const chunksRef = useRef([]);
  const streamRef = useRef(null);
  const tickRef = useRef(null);
  const startRef = useRef(0);
  const limitTimerRef = useRef(null);

  // Cleanup on unmount
  useEffect(() => () => {
    streamRef.current?.getTracks().forEach((t) => t.stop());
    clearInterval(tickRef.current);
    clearTimeout(limitTimerRef.current);
  }, []);

  const stop = useCallback(() => {
    if (mediaRef.current?.state === "recording") {
      mediaRef.current.stop();
    }
  }, []);

  const handleStop = useCallback(async () => {
    clearInterval(tickRef.current);
    clearTimeout(limitTimerRef.current);
    streamRef.current?.getTracks().forEach((t) => t.stop());
    const mime = mediaRef.current?.mimeType || "audio/webm";
    const blob = new Blob(chunksRef.current, { type: mime });
    if (blob.size === 0) { setState("idle"); setElapsed(0); return; }
    setState("processing");
    try {
      const ext = mime.includes("mp4") ? "mp4"
                : mime.includes("ogg") ? "ogg"
                : mime.includes("wav") ? "wav" : "webm";
      const fd = new FormData();
      fd.append("audio", blob, `${filenamePrefix}.${ext}`);
      Object.entries(extra || {}).forEach(([k, v]) => {
        if (v != null && v !== "") fd.append(k, v);
      });
      const { data } = await api.post(endpoint, fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: timeoutMs,
      });
      onResult?.(data);
      setState("idle");
      setElapsed(0);
    } catch (e) {
      const msg = e?.response?.data?.detail || "Voice processing failed — try again";
      toast.error(msg);
      setState("idle");
      setElapsed(0);
    }
  }, [endpoint, extra, filenamePrefix, onResult, timeoutMs]);

  const start = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const mime = MediaRecorder.isTypeSupported("audio/webm") ? "audio/webm" : "";
      const mr = new MediaRecorder(stream, mime ? { mimeType: mime } : undefined);
      mediaRef.current = mr;
      chunksRef.current = [];
      mr.ondataavailable = (e) => { if (e.data?.size > 0) chunksRef.current.push(e.data); };
      mr.onstop = handleStop;
      mr.start();
      startRef.current = Date.now();
      setElapsed(0);
      tickRef.current = setInterval(() => setElapsed(Date.now() - startRef.current), 200);
      limitTimerRef.current = setTimeout(() => {
        if (mediaRef.current?.state === "recording") {
          mediaRef.current.stop();
          toast.info("Recording limit reached — processing now");
        }
      }, limitMs);
      setState("recording");
    } catch {
      toast.error("Microphone permission denied");
    }
  }, [handleStop, limitMs]);

  const toggle = useCallback(() => {
    if (state === "idle") start();
    else if (state === "recording") stop();
  }, [state, start, stop]);

  const reset = useCallback(() => {
    setState("idle");
    setElapsed(0);
  }, []);

  const seconds = Math.floor(elapsed / 1000);
  const mm = String(Math.floor(seconds / 60)).padStart(2, "0");
  const ss = String(seconds % 60).padStart(2, "0");

  return {
    state,
    isIdle: state === "idle",
    isRecording: state === "recording",
    isProcessing: state === "processing",
    elapsed,
    mmss: `${mm}:${ss}`,
    start,
    stop,
    toggle,
    reset,
  };
}
